// JoinFunnel — /with/:slug
//
// A partner's branded front door. Their business name is on it, the company
// name is on it, and the visitor gives three things — name, email, phone —
// then lands directly in the secure intake. The client is created under the
// company and attributed to the partner the moment they submit.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ArrowRight, Lock } from "lucide-react";

export default function JoinFunnelPage() {
  const { slug } = useParams();
  const [info, setInfo] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`/api/with/${slug}`)
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d?.message || "This link is not active."); setInfo(d); })
      .catch((e) => setErr(e.message));
  }, [slug]);

  async function go() {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`/api/with/${slug}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), phone: phone.trim() || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || "Something went wrong — try again.");
      window.location.href = d.intake_url;
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }

  const partner = info?.partner?.business_name;
  const partnerLogo = info?.partner?.logo_url as string | null | undefined;
  const company = info?.company?.name;

  return (
    <div className="landing" style={{ minHeight: "100vh" }}>
      <header className="landing-nav">
        <div className="landing-shell landing-nav-inner">
          <span className="landing-logo" style={{ textDecoration: "none" }}>
            {company ? <>{company}</> : <>ALIEF <em>Funding Hub</em></>}
          </span>
          {partner && (
            <span className="muted small" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              in partnership with
              {partnerLogo && (
                <span style={{ display: "grid", placeItems: "center", width: 30, height: 30, borderRadius: 7, background: "#fff", overflow: "hidden" }}>
                  <img src={partnerLogo} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </span>
              )}
              <strong style={{ color: "var(--ne-text)" }}>{partner}</strong>
            </span>
          )}
        </div>
      </header>

      <section className="landing-section" style={{ paddingTop: 48 }}>
        <div className="landing-shell" style={{ maxWidth: 560 }}>
          {err && !info && (
            <div className="landing-card"><h1 style={{ fontSize: 22 }}>Link not active</h1><p className="muted">{err}</p></div>
          )}
          {info && (
            <div className="landing-card" style={{ padding: 32 }}>
              {partnerLogo && (
                <span style={{ display: "grid", placeItems: "center", width: 64, height: 64, borderRadius: 12, background: "#fff", overflow: "hidden", marginBottom: 14 }}>
                  <img src={partnerLogo} alt={`${partner} logo`} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </span>
              )}
              <p className="landing-kicker" style={{ marginBottom: 6 }}>{partner ? `Referred by ${partner}` : "Funding application"}</p>
              <h1 style={{ fontSize: 28, lineHeight: 1.15, marginBottom: 10 }}>Let's find out what you can get funded for.</h1>
              <p className="muted" style={{ marginBottom: 22 }}>
                Three quick details to get started, then a secure application that takes about ten minutes.
                Checking your options does <strong>not</strong> affect your credit score.
              </p>

              <div className="stacked-form">
                <label className="form-field"><span>Full name</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First and last name" autoComplete="name" /></label>
                <label className="form-field"><span>Email</span>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="email" placeholder="you@example.com" /></label>
                <label className="form-field"><span>Phone</span>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" placeholder="(555) 555-0142" /></label>
              </div>

              {err && <p className="error-text" style={{ marginTop: 10 }}>{err}</p>}
              <button className="primary-button" style={{ width: "100%", justifyContent: "center", marginTop: 18 }}
                disabled={busy || name.trim().split(/\s+/).length < 2 || !email.includes("@")} onClick={go}>
                {busy ? "Setting up your application…" : <>Start my application <ArrowRight size={16} aria-hidden="true" /></>}
              </button>

              <p className="muted small" style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <Lock size={14} aria-hidden="true" style={{ flex: "0 0 auto", marginTop: 2 }} />
                <span>Encrypted end to end. Your information is used only for your funding file and is never sold.</span>
              </p>
              <p className="muted small" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <ShieldCheck size={14} aria-hidden="true" style={{ flex: "0 0 auto", marginTop: 2 }} />
                <span>{company} is not a lender. All approvals, amounts, and terms are decided by the lenders you apply with.</span>
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
